import React, {useState, useEffect} from "react";
import { Span, Button, Checkbox } from "./../../";
import treeListStyles from "./TreeList.module.scss";
import radioStyles from "./../Forms/RadioButton.module.scss";
import _ from "lodash";
import classNames from "classnames";
import { DropdownArrowUpIcon, DropdownArrowDownIcon } from "@netapp/bxp-style/react-icons/Navigation";
// Padding to be added for the child items
const INCREMENT_LEFT_PADDING = 25;
// Types of selection,
// single -> one item selection
// singleRadio -> one item selection with Checkbox
// multi -> multiple Item selection
export const MenuType = Object.freeze({
    SINGLE: 'single',
    SINGLE_RADIO: 'singleRadio',
    MULTI: 'multi'
})
// Default dropdown Arrow Icon, can be configurable
const DefaultDropdownArrowUpIcon = (color) => {
    return <DropdownArrowUpIcon color={color} />;
};
// Default dropdown Arrow Icon, can be configurable
const DefaultDropdownArrowDownIcon = (color) => {
    return <DropdownArrowDownIcon color={color} />;
};
// This is comparison function, the comparison fails when icon and childrens are deeply compared so these values are omitted before comparison
const customizer = (objValue, othValue, uniqueIdentifier) => {
    return _.isEqual(objValue[uniqueIdentifier], othValue[uniqueIdentifier])
}

const getDefaultSelectedValues = (treeData, defaultSelectedValues, uniqueIdentifier, childKey) => {
    let defaultValues = [];
    treeData.map((treeItem) => {
        if (_.findIndex(defaultSelectedValues, (selectedValue) => _.isEqual(selectedValue[uniqueIdentifier], treeItem[uniqueIdentifier])) > -1) {
            defaultValues.push(treeItem);
        }
        if (!(_.isUndefined(treeItem[childKey]) || _.isEmpty(treeItem[childKey]))) {
            defaultValues.push(...getDefaultSelectedValues(treeItem[childKey], defaultSelectedValues, uniqueIdentifier, childKey));
        }
    });
    return defaultValues
}

// Intial selected value state manager function.
const valueToState = (options, selectedValues, uniqueIdentifier, childKey) => {

    let valueAsArray = [];

    valueAsArray = getDefaultSelectedValues(options, selectedValues, uniqueIdentifier, childKey);

    return {
        selectedOptions: valueAsArray,
    };
}

export const TreeItem = React.memo(({ element,
        childKey,
        index,
        leftSpace,
        dropdownUpIcon,
        dropdownDownIcon,
        menuType,
        isOptionDisabled,
        selectionState,
        updateSelectionState,
        isSelected,
        labelKey,
        uniqueIdentifier,
        iconRenderer,
        expandLevel,
        bottomBorder,
        customStyles,
        chevronPosition
    }) => {

    // Child can be an array or component to load on expand
    const hasChildren = !(_.isUndefined(element[childKey]) || _.isEmpty(element[childKey]));

    // Set true if tree item is collapsed.
    const [isCollapsed, setIsCollapsed] = React.useState(true);

    // provision to change the dropdown caret icons
    let DropdownUpIcon = dropdownUpIcon ?? DefaultDropdownArrowUpIcon;
    let DropdownDownIcon = dropdownDownIcon ?? DefaultDropdownArrowDownIcon;

    // provision to pass icon for each element
    let ItemIcon;
    if (iconRenderer) {
        ItemIcon = iconRenderer;
    }
    // check whether the element is disabled or not.
    const isDisabled = isOptionDisabled(element);

    useEffect(() => {
        if ((expandLevel === 'max') || (expandLevel > 0 && (_.toString(index).length <= expandLevel))) {
            setIsCollapsed(false);
        } else {
            setIsCollapsed(true);
        }
    }, [expandLevel]);


    // Onclick of element or checkbox update the selectionState with the selected element.
    // triggerOnChange is to notify parent component to trigger onchange event. 
    const onClick = () => {
        if (!isDisabled) {
            updateSelectionState(element, !isSelected);
        }
    };

    return  <>
                <div className={treeListStyles['tree-item-base']} style={{paddingLeft: leftSpace + 'px',
                                                                    borderBottom: bottomBorder ? '1px solid var(--border-main)' : 0,
                                                                    ...(customStyles?.treeItemBase ?? {})}} key={index}>
                    { (chevronPosition === 'left') && <div style={{paddingRight: '10px', visibility: hasChildren ? 'visible' : 'hidden'}}><Button style={{marginTop: '2px', ...(customStyles?.treeItemDropdownIcon ? customStyles?.treeItemDropdownIcon() : {})}} type="none" onClick={() => setIsCollapsed(!isCollapsed)}>
                        {isCollapsed ? <DropdownDownIcon /> : <DropdownUpIcon />}
                    </Button></div>}
                    <div className={treeListStyles['tree-item-title']} style={{...(customStyles?.treeItemTitle ?? {})}}>
                        {(_.toLower(menuType) === _.toLower(MenuType.MULTI) && !isDisabled) && <Checkbox
                                        checked={isSelected}
                                        disabled={isDisabled}
                                        name="row"
                                        className={treeListStyles['tree-item-checkbox']}
                                        onChange={onClick}
                                        style={{...(customStyles?.treeItemCheckbox ?? {})}}/>
                        }
                        {(_.toLower(menuType) === _.toLower(MenuType.SINGLE_RADIO)) && !isDisabled &&
                            <label className={classNames({ [treeListStyles['tree-item-radio']]: true, [radioStyles["disabled"]]: isDisabled, [radioStyles["active"]]: isSelected })}
                                style={{...(customStyles?.treeItemRadio ?? {})}}>
                                    <input key={ index + isSelected } checked={isSelected} onChange={onClick} type="radio" name="radio" disabled={isDisabled}/>
                            </label>
                        }
                        <div onClick={() => onClick()} className={treeListStyles['tree-item-label']} style={{...(customStyles?.treeItemLabel ?? {})}}>
                            {ItemIcon && <ItemIcon {...element} isSelected={isSelected}/>}
                            <Span level="16"
                                ellipsis={true}
                                title={_.get(element, labelKey)}
                                color={isDisabled && !hasChildren ? "text-disabled" : (isSelected ? 'info' : 'text-primary')}
                                style={{ width: 'calc(95%)', paddingLeft: (ItemIcon && !(_.isNull(ItemIcon(element))))? '10px' : '0px'}}>
                                    {_.get(element, labelKey)}
                            </Span>
                        </div>
                        
                    </div>
                    {hasChildren && (chevronPosition === 'right') && <div style={{paddingRight: '20px'}}><Button style={{marginBottom: '2px', ...(customStyles?.treeItemDropdownIcon ? customStyles?.treeItemDropdownIcon() : {})}} type="none" onClick={() => setIsCollapsed(!isCollapsed)}>
                        {isCollapsed ? <DropdownDownIcon /> : <DropdownUpIcon />}
                    </Button></div>}
                </div>
                {
                    hasChildren && !isCollapsed && _.map(element[childKey], (childRow, i) => {
                        const isSelected = (_.findIndex(selectionState.selectedOptions, (value) => _.isEqualWith(value, childRow, (objValue, othValue) => customizer(objValue, othValue, uniqueIdentifier)))) > -1;
                        return <TreeItem
                                    element={childRow}
                                    childKey={childKey}
                                    index={`${index}${i}`}
                                    key={`${index}${i}`}
                                    leftSpace={leftSpace + INCREMENT_LEFT_PADDING}
                                    menuType={menuType}
                                    dropdownUpIcon={dropdownUpIcon}
                                    dropdownDownIcon={dropdownDownIcon}
                                    isOptionDisabled={isOptionDisabled}
                                    selectionState={selectionState}
                                    isSelected={isSelected}
                                    updateSelectionState={updateSelectionState}
                                    labelKey={labelKey}
                                    uniqueIdentifier={uniqueIdentifier}
                                    iconRenderer={iconRenderer}
                                    expandLevel={expandLevel}
                                    bottomBorder={bottomBorder}
                                    customStyles={customStyles}
                                    chevronPosition={chevronPosition}>
                                </TreeItem>
                        })}
            </>
})

const TreeList = React.memo(React.forwardRef(({
    treeData,
    childKey = 'childrens',
    labelKey = 'label',
    dropdownUpIcon,
    dropdownDownIcon,
    menuType = MenuType.SINGLE,
    selectedValues,
    isOptionDisabled,
    onChange,
    uniqueIdentifier,
    iconRenderer,
    expandLevel,
    bottomBorder,
    customStyles,
}, forwardRef) => {
    const isMulti = (_.toLower(menuType) === _.toLower(MenuType.MULTI));
    // set the defaultSelectedValues as initial value
    const initialValue = valueToState(treeData, selectedValues, uniqueIdentifier, childKey);
    const [selection, setSelection] = useState(initialValue);
    // toggle this to trigger onchange event with selected values as param to parent components
    const [triggerChange, setTriggerChange] = useState(false);
    // Default disable function
    const disableOption = (element) => {
        return element?.isDisabled
    }
    // Parent component can override the disable option according to their requirement
    isOptionDisabled = isOptionDisabled ?? disableOption;

    // trigger change will call the onChange callback function, which is an input parameter
    useEffect(() => {
        if (onChange) onChange(selection.selectedOptions);
    }, [triggerChange]);

    // Update the Selection state which stores the current selected values.
    const updateSelectionState = (option, selected) => {
        // If it is multi select, when checkbox is checked, checking whether the element already exist in selected values and push only if is doesn't exist
        if (isMulti) {
            const alreadyExist = (_.findIndex(selection.selectedOptions, (value) => _.isEqualWith(value, option, (objValue, othValue) => customizer(objValue, othValue, uniqueIdentifier)))) > -1;
            if (selected && !alreadyExist) {
                setSelection((currentSelection) => {
                    return {
                        ...currentSelection,
                        selectedOptions: [...selection.selectedOptions, option]
                    }
                })
            } else if (!selected) {
                setSelection((currentSelection) => {
                    return {
                        ...currentSelection,
                        selectedOptions: (_.reject(selection.selectedOptions, (value) => _.isEqualWith(value, option, (objValue, othValue) => customizer(objValue, othValue, uniqueIdentifier))))
                    }
                })
            }
        } else { // If it is single select, replace the state with the selected tree item
            setSelection((currentSelection) => {
                return {
                    ...currentSelection,
                    selectedOptions: [option]
                }
            })
        }
        // Toggle this to update the selected values to parent component through onchange event.
        setTriggerChange(!triggerChange);
    }

    return <div ref={forwardRef} className={classNames(treeListStyles['tree-list-base'])} style={{...(customStyles?.treeListBase ?? {})}}>
        {treeData.map((ele, index) => {
                const isSelected = (_.findIndex(selection.selectedOptions, (value) => _.isEqualWith(value, ele, (objValue, othValue) => customizer(objValue, othValue, uniqueIdentifier)))) > -1;
                return <TreeItem
                    key={index}
                    element={ele}
                    childKey={childKey}
                    index={index}
                    leftSpace={10}
                    dropdownUpIcon={dropdownUpIcon}
                    dropdownDownIcon={dropdownDownIcon}
                    menuType={menuType}
                    isOptionDisabled={isOptionDisabled}
                    isSelected={isSelected}
                    selectionState={selection}
                    updateSelectionState={updateSelectionState}
                    labelKey={labelKey}
                    uniqueIdentifier={uniqueIdentifier}
                    iconRenderer={iconRenderer}
                    expandLevel={expandLevel}
                    bottomBorder={bottomBorder}
                    customStyles={customStyles}
                    chevronPosition={'left'}>
                </TreeItem>
            }
        )}
    </div>
}))

export default TreeList;